import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, PageHeader, Spinner, Badge, Select, Button } from "@/components/ui";
import { toast } from "sonner";
import { Copy, Download, Network, ArrowRightLeft } from "lucide-react";

const FORMATS = [
  { key: "fhir", label: "FHIR R4 Patient", badge: "US Core", lang: "json" },
  { key: "hl7", label: "HL7 v2 ADT^A08", badge: "v2.5.1", lang: "hl7" },
  { key: "x12", label: "X12 837 Claim", badge: "5010", lang: "edi" },
];

export default function Interoperability() {
  const [members, setMembers] = useState([]);
  const [claims, setClaims] = useState([]);
  const [memberId, setMemberId] = useState("");
  const [claimId, setClaimId] = useState("");
  const [format, setFormat] = useState("fhir");
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/members").then((r) => { setMembers(r.data); if (r.data[0]) setMemberId(r.data[0].id); });
    api.get("/claims").then((r) => { setClaims(r.data); if (r.data[0]) setClaimId(r.data[0].id); });
  }, []);

  const fetchOutput = async () => {
    setBusy(true); setOutput("");
    try {
      if (format === "fhir") {
        const { data } = await api.get(`/interop/fhir/Patient/${memberId}`);
        setOutput(JSON.stringify(data, null, 2));
      } else if (format === "hl7") {
        const { data } = await api.get(`/interop/hl7/adt/${memberId}`);
        setOutput(data.message);
      } else {
        const { data } = await api.get(`/interop/x12/${claimId}`);
        setOutput(data.message);
      }
    } catch { toast.error("Failed to generate representation"); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if ((format !== "x12" && memberId) || (format === "x12" && claimId)) fetchOutput();
    // eslint-disable-next-line
  }, [format, memberId, claimId]);

  const copy = () => { navigator.clipboard.writeText(output); toast.success("Copied to clipboard"); };
  const download = () => {
    const ext = format === "fhir" ? "json" : format === "hl7" ? "hl7" : "edi";
    const blob = new Blob([output], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `gomco-${format}.${ext}`; a.click();
  };

  return (
    <div className="vc-fade-up">
      <PageHeader title="Interoperability Gateway" subtitle="Standards-based data exchange: FHIR R4 (US Core), HL7 v2, and ANSI X12 5010 — with configurable mapping to state schemas." />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
        {[["FHIR R4 US Core", "REST + Bundle export"], ["HL7 v2 / v3", "ADT, ORU, SIU messaging"], ["X12 5010", "837 / 835 / 834 / 270-271"], ["Transport", "SFTP · REST · MLLP"]].map(([t, d]) => (
          <Card key={t} className="p-4"><div className="flex items-center gap-2 mb-1"><ArrowRightLeft className="h-4 w-4 text-brand-700" /><p className="font-semibold text-sm">{t}</p></div><p className="text-xs text-muted-foreground">{d}</p></Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-1">
          <h3 className="font-display font-semibold flex items-center gap-2 mb-4"><Network className="h-4 w-4 text-brand-700" /> Generate Representation</h3>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Format</label>
              <div className="mt-2 space-y-1.5">
                {FORMATS.map((f) => (
                  <button key={f.key} onClick={() => setFormat(f.key)} data-testid={`interop-format-${f.key}`}
                    className={`w-full flex items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors ${format === f.key ? "border-brand-600 bg-brand-50 text-brand-800" : "border-border hover:bg-muted"}`}>
                    {f.label}<Badge tone="Low">{f.badge}</Badge>
                  </button>
                ))}
              </div>
            </div>
            {format !== "x12" ? (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Member</label>
                <Select className="w-full mt-1.5" value={memberId} onChange={(e) => setMemberId(e.target.value)} data-testid="interop-member">
                  {members.map((m) => <option key={m.id} value={m.id}>{`${m.first_name} ${m.last_name} (${m.mrn})`}</option>)}
                </Select>
              </div>
            ) : (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Claim</label>
                <Select className="w-full mt-1.5" value={claimId} onChange={(e) => setClaimId(e.target.value)} data-testid="interop-claim">
                  {claims.map((c) => <option key={c.id} value={c.id}>{`${c.claim_number} — ${c.member_name}`}</option>)}
                </Select>
              </div>
            )}
          </div>
        </Card>

        <Card className="p-0 lg:col-span-2 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-border">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Output</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={copy} disabled={!output} data-testid="interop-copy"><Copy className="h-3.5 w-3.5" /> Copy</Button>
              <Button size="sm" variant="outline" onClick={download} disabled={!output} data-testid="interop-download"><Download className="h-3.5 w-3.5" /> Download</Button>
            </div>
          </div>
          <div className="p-4 bg-brand-950 min-h-[420px] max-h-[520px] overflow-auto">
            {busy ? <Spinner label="Generating…" /> : <pre className="font-mono text-xs text-brand-100 whitespace-pre-wrap leading-relaxed" data-testid="interop-output">{output}</pre>}
          </div>
        </Card>
      </div>
    </div>
  );
}
